import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { StepperInput } from '../components/ui/stepper-input';
import { NumericInput } from '../components/ui/numeric-input';
import { 
  ChevronLeft, Car, Upload, X, Calendar as CalendarIcon, Save, AlertTriangle, Shield, Info,
  GripVertical, Trash2, RefreshCw, Zap, FileText, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays } from 'date-fns';
import { getVehiclePhotoUrl, filterValidPhotos, handleVehicleImageError } from '../lib/imageUtils';
import TrackerSection from '../components/TrackerSection';

// Australian states for registration
const AUSTRALIAN_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'SA', label: 'South Australia' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'NT', label: 'Northern Territory' },
  { value: 'ACT', label: 'Australian Capital Territory' },
];

// Cancellation policies
const CANCELLATION_POLICIES = [
  { 
    value: 'flexible', 
    label: 'Flexible',
    description: '100% refund if cancelled 24h+ before start'
  },
  { 
    value: 'moderate', 
    label: 'Moderate',
    description: '100% refund 5+ days, 50% refund 2-5 days, 0% within 48h'
  },
  { 
    value: 'strict', 
    label: 'Strict',
    description: '50% refund 7+ days, 0% refund within 7 days'
  },
];

/**
 * Capture a still frame from the given video File and return a JPEG Blob.
 * Best-effort — resolves to null on any failure (codec, CORS, mobile quirks).
 * Used to seed a poster/thumbnail on listing video uploads.
 */
async function captureVideoPoster(file) {
  return new Promise((resolve) => {
    let url = null;
    let video = null;
    const cleanup = () => {
      try { if (url) URL.revokeObjectURL(url); } catch (_) {}
      try { if (video) { video.removeAttribute('src'); video.load(); } } catch (_) {}
    };
    const giveUp = () => { cleanup(); resolve(null); };
    try {
      url = URL.createObjectURL(file);
      video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.crossOrigin = 'anonymous';
      video.src = url;

      const captureFrame = () => {
        try {
          const w = video.videoWidth || 640;
          const h = video.videoHeight || 360;
          // Cap longest edge at 960px — keeps poster small on mobile while
          // still looking sharp in the gallery + thumbnail strip.
          const maxEdge = 960;
          const scale = Math.min(1, maxEdge / Math.max(w, h));
          const cw = Math.max(1, Math.round(w * scale));
          const ch = Math.max(1, Math.round(h * scale));
          const canvas = document.createElement('canvas');
          canvas.width = cw;
          canvas.height = ch;
          const ctx = canvas.getContext('2d');
          if (!ctx) return giveUp();
          ctx.drawImage(video, 0, 0, cw, ch);
          canvas.toBlob((blob) => { cleanup(); resolve(blob || null); }, 'image/jpeg', 0.82);
        } catch (_) {
          giveUp();
        }
      };

      video.onloadedmetadata = () => {
        // Seek a tiny bit in to avoid an empty/black first frame.
        const target = Math.min(0.5, Math.max(0, (video.duration || 1) * 0.1));
        try { video.currentTime = target; } catch (_) { captureFrame(); }
      };
      video.onseeked = captureFrame;
      video.onerror = giveUp;
      // Hard timeout in case mobile browser stalls
      setTimeout(giveUp, 6000);
    } catch (_) {
      giveUp();
    }
  });
}

const VehicleForm = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const isEditing = !!vehicleId;
  
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [blockReason, setBlockReason] = useState('maintenance');
  
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    registration: '',
    vehicle_category: 'car',
    vehicle_type: 'sedan',
    description: '',
    location: '',
    daily_rate: '',
    min_booking_days: 1,
    max_booking_days: 30,
    kms_included: '',
    excess_km_fee: '',
    bond_amount: '',
    photos: [],
    // P0 Safety Controls - Required fields
    rego_expiry_date: '',
    rego_state: 'NSW',
    // Insurance compliance
    insurance_provider: '',
    insurance_policy_number: '',
    insurance_expiry_date: '',
    insurance_document_s3_key: '',
    cancellation_policy: 'moderate',
    // Booking control
    instant_book: false,
    // Additional host settings
    minimum_driver_age: 21,
    detailing_fee: '',
    late_return_fee: '',
    // Phase 2A: Category specs
    category_specs: {},
    // Specialty asset host-controllable rules (stored at top level).
    // Rendered only when vehicle_category === 'specialty_asset'.
    specialty_rules: {},
    // Optional renter-facing listing video (single S3 key). Managed via
    // dedicated /video endpoints — not sent in the vehicle PUT payload.
    listing_video_s3_key: null
  });
  
  // Host defaults and rule overrides for the Default + Override hierarchy
  const [hostDefaults, setHostDefaults] = useState(null);
  const [ruleOverrides, setRuleOverrides] = useState({
    minimum_driver_age: false,
    kms_included: false,
    excess_km_fee: false,
    late_return_fee: false,
    detailing_fee: false
  });
  
  const [regoDatePickerOpen, setRegoDatePickerOpen] = useState(false);
  const [insuranceDatePickerOpen, setInsuranceDatePickerOpen] = useState(false);
  const [insuranceUploading, setInsuranceUploading] = useState(false);

  // Fetch host defaults for new vehicles
  useEffect(() => {
    fetchHostDefaults();
  }, []);

  useEffect(() => {
    if (isEditing) {
      fetchVehicle();
      fetchAvailability();
    } else if (hostDefaults) {
      // New vehicle - auto-populate from host defaults
      setFormData(prev => ({
        ...prev,
        minimum_driver_age: hostDefaults.minimum_driver_age || 21,
        kms_included: hostDefaults.kms_included || 200,
        excess_km_fee: hostDefaults.excess_km_fee || 0.35,
        late_return_fee: hostDefaults.late_return_fee || 50,
        detailing_fee: hostDefaults.detailing_fee || 0
      }));
    }
  }, [vehicleId, hostDefaults]);

  const fetchHostDefaults = async () => {
    try {
      const response = await api.get('/users/host-rules/defaults');
      setHostDefaults(response.data);
    } catch (error) {
      console.error('Error fetching host defaults:', error);
    }
  };

  const fetchVehicle = async () => {
    try {
      const response = await api.get(`/vehicles/${vehicleId}`);
      const v = response.data;
      setFormData({
        make: v.make || '',
        model: v.model || '',
        year: v.year || new Date().getFullYear(),
        registration: v.registration || '',
        vehicle_category: v.vehicle_category || 'car',
        vehicle_type: v.vehicle_type || 'sedan',
        description: v.description || '',
        location: v.location || '',
        daily_rate: v.daily_rate || '',
        min_booking_days: v.min_booking_days || 1,
        max_booking_days: v.max_booking_days || 30,
        kms_included: v.kms_included || '',
        excess_km_fee: v.excess_km_fee || '',
        bond_amount: v.bond_amount || '',
        photos: v.photos || [],
        // P0 Safety Controls
        rego_expiry_date: v.rego_expiry_date || '',
        rego_state: v.rego_state || 'NSW',
        // Insurance compliance
        insurance_provider: v.insurance_provider || '',
        insurance_policy_number: v.insurance_policy_number || '',
        insurance_expiry_date: v.insurance_expiry_date || '',
        insurance_document_s3_key: v.insurance_document_s3_key || '',
        cancellation_policy: v.cancellation_policy || 'moderate',
        // Booking control
        instant_book: v.instant_book || false,
        // Additional settings
        minimum_driver_age: v.minimum_driver_age || 21,
        detailing_fee: v.detailing_fee || '',
        late_return_fee: v.late_return_fee || '',
        // GPS Tracker fields (read-only in form, managed via TrackerSection)
        vehicle_id: v.vehicle_id,
        tracker_device_id: v.tracker_device_id || null,
        tracker_provider: v.tracker_provider || null,
        tracker_status: v.tracker_status || null,
        recovery_mode_active: v.recovery_mode_active || false,
        // Phase 2A: Category specs
        category_specs: v.category_specs || {},
        specialty_rules: v.specialty_rules || {},
        listing_video_s3_key: v.listing_video_s3_key || null
      });
      // Load rule overrides if present
      if (v.rule_overrides) {
        setRuleOverrides(v.rule_overrides);
      }
    } catch (error) {
      toast.error('Vehicle not found');
      navigate('/host/vehicles');
    }
  };

  const fetchAvailability = async () => {
    try {
      const response = await api.get(`/vehicles/${vehicleId}/availability`);
      setAvailability(response.data.availability || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required P0 fields
    if (!formData.rego_expiry_date) {
      toast.error('Registration expiry date is required');
      return;
    }
    if (!formData.cancellation_policy) {
      toast.error('Cancellation policy is required');
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        ...formData,
        daily_rate: parseFloat(formData.daily_rate),
        kms_included: formData.kms_included ? parseInt(formData.kms_included) : null,
        excess_km_fee: formData.excess_km_fee ? parseFloat(formData.excess_km_fee) : null,
        bond_amount: formData.bond_amount ? parseFloat(formData.bond_amount) : null,
        // Include rule overrides state
        rule_overrides: ruleOverrides
      };

      if (isEditing) {
        await api.put(`/vehicles/${vehicleId}`, payload);
        toast.success('Vehicle updated successfully');
      } else {
        const response = await api.post('/vehicles', payload);
        // Check for rego warning in response
        if (response.data.rego_warning) {
          toast.warning(response.data.rego_warning);
        }
        toast.success('Vehicle created successfully');
        navigate(`/host/vehicles/${response.data.vehicle_id}/edit`);
        return;
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Failed to save vehicle';
      // P0 FIX: If contact details are missing, redirect to profile page
      if (errorDetail.includes('contact details') && errorDetail.includes('Missing:')) {
        toast.error(
          <div>
            <p className="font-medium">Complete your profile first</p>
            <p className="text-sm mt-1">{errorDetail}</p>
            <Button 
              size="sm" 
              className="mt-2"
              onClick={() => navigate('/host/account')}
            >
              Go to Account Settings
            </Button>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !isEditing) return;

    setUploadingPhoto(true);
    const uploadedKeys = [];
    const failed = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      try {
        // P0 FIX: Delete Content-Type header so axios auto-generates multipart/form-data with boundary
        const response = await api.post(`/vehicles/${vehicleId}/photos`, formDataUpload, {
          headers: { 'Content-Type': undefined }
        });
        const newPhotoKey = response.data.s3_key;
        if (newPhotoKey) uploadedKeys.push(newPhotoKey);
      } catch (error) {
        let detail = error?.response?.data?.detail;
        if (typeof detail === 'object') detail = JSON.stringify(detail);
        detail = detail || error?.message || 'Unknown error';
        failed.push(`${file.name}: ${detail}`);
      }
    }

    if (uploadedKeys.length > 0) {
      setFormData(prev => ({ ...prev, photos: [...prev.photos, ...uploadedKeys] }));
      toast.success(`${uploadedKeys.length} photo${uploadedKeys.length > 1 ? 's' : ''} uploaded`);
    }
    if (failed.length > 0) {
      toast.error(`Failed: ${failed.slice(0, 3).join('; ')}${failed.length > 3 ? '…' : ''}`);
    }
    // Reset input so selecting the same files again re-triggers change
    try { e.target.value = ''; } catch (_) {}
    setUploadingPhoto(false);
  };

  const handlePhotoDelete = async (photoIndex) => {
    if (!isEditing) return;
    
    try {
      await api.delete(`/vehicles/${vehicleId}/photos/${photoIndex}`);
      setFormData(prev => ({
        ...prev,
        photos: prev.photos.filter((_, i) => i !== photoIndex)
      }));
      toast.success('Photo deleted');
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to delete photo: ${detail}`);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !isEditing) return;
    // Client-side size check to fail fast on mobile before hitting server
    const MAX_BYTES = 50 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      toast.error('Video is too large. Max 50MB');
      try { e.target.value = ''; } catch (_) {}
      return;
    }

    setUploadingVideo(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    try {
      const response = await api.post(`/vehicles/${vehicleId}/video`, formDataUpload, {
        headers: { 'Content-Type': undefined }
      });
      const newVideoKey = response.data.s3_key;
      // Backend cleared any prior poster — reflect that in local state.
      setFormData(prev => ({ ...prev, listing_video_s3_key: newVideoKey, listing_video_poster_s3_key: null }));
      toast.success('Video uploaded');

      // Best-effort: capture a still-frame poster from the uploaded file
      // and POST it to the dedicated poster endpoint. Any failure is silent
      // — the gallery falls back gracefully when no poster exists.
      try {
        const posterBlob = await captureVideoPoster(file);
        if (posterBlob) {
          const posterForm = new FormData();
          posterForm.append('file', posterBlob, 'poster.jpg');
          const presp = await api.post(`/vehicles/${vehicleId}/video/poster`, posterForm, {
            headers: { 'Content-Type': undefined }
          });
          if (presp?.data?.s3_key) {
            setFormData(prev => ({ ...prev, listing_video_poster_s3_key: presp.data.s3_key }));
          }
        }
      } catch (_) {
        // Silent — poster is enhancement-only.
      }
    } catch (error) {
      let detail = error?.response?.data?.detail;
      if (typeof detail === 'object') detail = JSON.stringify(detail);
      detail = detail || error?.message || 'Unknown error';
      toast.error(`Failed to upload video: ${detail}`);
    } finally {
      try { e.target.value = ''; } catch (_) {}
      setUploadingVideo(false);
    }
  };

  const handleVideoDelete = async () => {
    if (!isEditing) return;
    try {
      await api.delete(`/vehicles/${vehicleId}/video`);
      setFormData(prev => ({ ...prev, listing_video_s3_key: null, listing_video_poster_s3_key: null }));
      toast.success('Video deleted');
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to delete video: ${detail}`);
    }
  };

  const handlePhotoDragStart = (e, index) => {
    e.dataTransfer.setData('photoIndex', index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePhotoDrop = async (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('photoIndex'));
    if (sourceIndex === targetIndex) return;

    const photos = [...formData.photos];
    const [movedPhoto] = photos.splice(sourceIndex, 1);
    photos.splice(targetIndex, 0, movedPhoto);

    // Update local state immediately
    setFormData(prev => ({ ...prev, photos }));

    // Sync with backend
    try {
      const newOrder = photos.map((_, i) => {
        // Find the original index of each photo
        const originalIndex = formData.photos.indexOf(photos[i]);
        return originalIndex;
      });
      await api.post(`/vehicles/${vehicleId}/photos/reorder`, { photo_order: newOrder });
      toast.success('Photos reordered');
    } catch (error) {
      // Revert on error
      setFormData(prev => ({ ...prev, photos: formData.photos }));
      toast.error('Failed to reorder photos');
    }
  };

  const handleInstantBookToggle = async (enabled) => {
    setFormData(prev => ({ ...prev, instant_book: enabled }));
    
    if (isEditing) {
      try {
        await api.post(`/vehicles/${vehicleId}/instant-book`, { instant_book: enabled });
        toast.success(`Instant Book ${enabled ? 'enabled' : 'disabled'}`);
      } catch (error) {
        setFormData(prev => ({ ...prev, instant_book: !enabled }));
        toast.error('Failed to update Instant Book setting');
      }
    }
  };

  const handleBlockDates = async () => {
    if (!selectedDates.length || !isEditing) return;

    try {
      const dates = selectedDates.map(d => format(d, 'yyyy-MM-dd'));
      await api.post(`/vehicles/${vehicleId}/availability/bulk`, {
        dates,
        is_available: false,
        reason: blockReason
      });
      toast.success('Dates blocked');
      setSelectedDates([]);
      fetchAvailability();
    } catch (error) {
      toast.error('Failed to block dates');
    }
  };

  const handleUnblockDate = async (date) => {
    try {
      await api.post(`/vehicles/${vehicleId}/availability`, {
        date,
        is_available: true,
        reason: null
      });
      toast.success('Date unblocked');
      fetchAvailability();
    } catch (error) {
      toast.error('Failed to unblock date');
    }
  };

  const blockedDates = availability.filter(a => !a.is_available);

  const vehicleTypes = [
    { value: 'sedan', label: 'Sedan' },
    { value: 'suv', label: 'SUV' },
    { value: 'ute', label: 'Ute' },
    { value: 'van', label: 'Van' },
    { value: 'luxury', label: 'Luxury' }
  ];

  const vehicleCategories = [
    { value: 'car', label: 'Car' },
    { value: 'motorcycle', label: 'Motorcycle' },
    { value: 'boat', label: 'Boat' },
    { value: 'jetski', label: 'Jet Ski' },
    { value: 'trailer', label: 'Trailer' },
    { value: 'ebike', label: 'eBike' },
    { value: 'electric_scooter', label: 'Electric Scooter' },
    { value: 'mobility_scooter', label: 'Mobility Scooter' },
    { value: 'specialty_asset', label: 'Specialty / Income-Generating Asset (e.g. Coffee Trailer)' }
  ];

  // Phase 2A: Helper to update a single category_specs field
  const setSpec = (key, value) => {
    setFormData(prev => ({ ...prev, category_specs: { ...prev.category_specs, [key]: value } }));
  };

  // Specialty asset — helper to update a single specialty_rules field.
  const setSpecialtyRule = (key, value) => {
    setFormData(prev => ({ ...prev, specialty_rules: { ...prev.specialty_rules, [key]: value } }));
  };

  const blockReasons = [
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'holiday', label: 'Holiday' },
    { value: 'external_booking', label: 'External Booking' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 flex items-center gap-3 sm:gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/host/vehicles')}
            className="h-9 w-9 sm:h-10 sm:w-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-lg sm:text-xl font-semibold">
            {isEditing ? 'Edit Vehicle' : 'Add New Vehicle'}
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-4 sm:py-6 lg:py-8">
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Basic Info */}
          <Card className="border-slate-100">
            <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
              <CardTitle className="font-heading text-base sm:text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="make" className="text-sm">
                    {formData.vehicle_category === 'specialty_asset' ? 'Brand / Operator *' : 'Make *'}
                  </Label>
                  <Input
                    id="make"
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    placeholder={formData.vehicle_category === 'specialty_asset' ? 'e.g., Our Kaapi Story' : 'e.g., Toyota'}
                    required
                    className="mt-1.5 h-10 sm:h-11"
                    data-testid="make-input"
                  />
                </div>
                <div>
                  <Label htmlFor="model" className="text-sm">
                    {formData.vehicle_category === 'specialty_asset' ? 'Asset Name *' : 'Model *'}
                  </Label>
                  <Input
                    id="model"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder={formData.vehicle_category === 'specialty_asset' ? 'e.g., Coffee Trailer' : 'e.g., Camry'}
                    required
                    className="mt-1.5 h-10 sm:h-11"
                    data-testid="model-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="year" className="text-sm">Year *</Label>
                  <Input
                    id="year"
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    required
                    className="mt-1.5 h-10 sm:h-11"
                    data-testid="year-input"
                  />
                </div>
                <div>
                  <Label htmlFor="registration" className="text-sm">Registration *</Label>
                  <Input
                    id="registration"
                    value={formData.registration}
                    onChange={(e) => setFormData({ ...formData, registration: e.target.value })}
                    placeholder="ABC123"
                    required
                    className="mt-1.5 h-10 sm:h-11"
                    data-testid="registration-input"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-sm">What are you listing? *</Label>
                  <Select 
                    value={formData.vehicle_category} 
                    onValueChange={(value) => setFormData({ ...formData, vehicle_category: value })}
                  >
                    <SelectTrigger className="mt-1.5 h-10 sm:h-11" data-testid="category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleCategories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {formData.vehicle_category === 'car' && (
                <div>
                  <Label className="text-sm">Vehicle Type *</Label>
                  <Select 
                    value={formData.vehicle_type} 
                    onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                  >
                    <SelectTrigger className="mt-1.5 h-10 sm:h-11" data-testid="type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Phase 2A: Category-Specific Vehicle Specifications */}
              {formData.vehicle_category === 'car' && (
                <div className="p-3 bg-slate-50 rounded-lg space-y-3" data-testid="specs-car">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Car Specifications</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Seats</Label>
                      <Input type="number" min={1} max={12} placeholder="5" className="mt-1 h-9 text-sm" data-testid="spec-seats"
                        value={formData.category_specs.seats || ''} onChange={e => setSpec('seats', e.target.value ? parseInt(e.target.value) : '')} />
                    </div>
                    <div>
                      <Label className="text-xs">Transmission</Label>
                      <Select value={formData.category_specs.transmission || ''} onValueChange={v => setSpec('transmission', v)}>
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-transmission"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="automatic">Automatic</SelectItem>
                          <SelectItem value="manual">Manual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Fuel Type</Label>
                      <Select value={formData.category_specs.fuel_type || ''} onValueChange={v => setSpec('fuel_type', v)}>
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-fuel"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="petrol">Petrol</SelectItem>
                          <SelectItem value="diesel">Diesel</SelectItem>
                          <SelectItem value="electric">Electric</SelectItem>
                          <SelectItem value="hybrid">Hybrid</SelectItem>
                          <SelectItem value="lpg">LPG</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {formData.vehicle_category === 'motorcycle' && (
                <div className="p-3 bg-slate-50 rounded-lg space-y-3" data-testid="specs-motorcycle">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Motorcycle Specifications</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Motorcycle Type</Label>
                      <Select value={formData.category_specs.motorcycle_type || ''} onValueChange={v => setSpec('motorcycle_type', v)}>
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-motorcycle-type"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dirt">Dirt</SelectItem>
                          <SelectItem value="sport">Sport</SelectItem>
                          <SelectItem value="cruiser">Cruiser</SelectItem>
                          <SelectItem value="touring">Touring</SelectItem>
                          <SelectItem value="adventure">Adventure</SelectItem>
                          <SelectItem value="naked">Naked / Street</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Engine CC</Label>
                      <Input type="number" min={50} placeholder="600" className="mt-1 h-9 text-sm" data-testid="spec-engine-cc"
                        value={formData.category_specs.engine_cc || ''} onChange={e => setSpec('engine_cc', e.target.value ? parseInt(e.target.value) : '')} />
                    </div>
                    <div>
                      <Label className="text-xs">Licence Class Required</Label>
                      <Select value={formData.category_specs.licence_class_required || ''} onValueChange={v => setSpec('licence_class_required', v)}>
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-licence-class"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="R-E">R-E (Learner)</SelectItem>
                          <SelectItem value="R">R (Full)</SelectItem>
                          <SelectItem value="any">Any motorcycle licence</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {formData.vehicle_category === 'boat' && (
                <div className="p-3 bg-slate-50 rounded-lg space-y-3" data-testid="specs-boat">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Boat Specifications</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Boat Type</Label>
                      <Select value={formData.category_specs.boat_type || ''} onValueChange={v => setSpec('boat_type', v)}>
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-boat-type"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="runabout">Runabout</SelectItem>
                          <SelectItem value="fishing">Fishing Boat</SelectItem>
                          <SelectItem value="pontoon">Pontoon</SelectItem>
                          <SelectItem value="bowrider">Bowrider</SelectItem>
                          <SelectItem value="cabin_cruiser">Cabin Cruiser</SelectItem>
                          <SelectItem value="sailboat">Sailboat</SelectItem>
                          <SelectItem value="inflatable">Inflatable</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Passenger Capacity</Label>
                      <Input type="number" min={1} placeholder="6" className="mt-1 h-9 text-sm" data-testid="spec-passenger-capacity"
                        value={formData.category_specs.passenger_capacity || ''} onChange={e => setSpec('passenger_capacity', e.target.value ? parseInt(e.target.value) : '')} />
                    </div>
                    <div>
                      <Label className="text-xs">Hull Length (m)</Label>
                      <Input type="number" step="0.1" min={1} placeholder="5.5" className="mt-1 h-9 text-sm" data-testid="spec-hull-length"
                        value={formData.category_specs.hull_length || ''} onChange={e => setSpec('hull_length', e.target.value ? parseFloat(e.target.value) : '')} />
                    </div>
                    <div className="flex items-center gap-2 pt-4">
                      <input type="checkbox" id="boat-licence" className="rounded border-slate-300" data-testid="spec-boat-licence"
                        checked={formData.category_specs.boat_licence_required || false} onChange={e => setSpec('boat_licence_required', e.target.checked)} />
                      <Label htmlFor="boat-licence" className="text-xs cursor-pointer">Boat licence required</Label>
                    </div>
                  </div>
                </div>
              )}

              {formData.vehicle_category === 'jetski' && (
                <div className="p-3 bg-slate-50 rounded-lg space-y-3" data-testid="specs-jetski">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Jet Ski Specifications</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Engine CC</Label>
                      <Input type="number" min={50} placeholder="1500" className="mt-1 h-9 text-sm" data-testid="spec-jetski-cc"
                        value={formData.category_specs.engine_cc || ''} onChange={e => setSpec('engine_cc', e.target.value ? parseInt(e.target.value) : '')} />
                    </div>
                    <div>
                      <Label className="text-xs">Rider Capacity</Label>
                      <Input type="number" min={1} max={4} placeholder="2" className="mt-1 h-9 text-sm" data-testid="spec-rider-capacity"
                        value={formData.category_specs.rider_capacity || ''} onChange={e => setSpec('rider_capacity', e.target.value ? parseInt(e.target.value) : '')} />
                    </div>
                    <div className="flex items-center gap-2 pt-4">
                      <input type="checkbox" id="jetski-licence" className="rounded border-slate-300" data-testid="spec-jetski-licence"
                        checked={formData.category_specs.licence_required || false} onChange={e => setSpec('licence_required', e.target.checked)} />
                      <Label htmlFor="jetski-licence" className="text-xs cursor-pointer">Licence required</Label>
                    </div>
                  </div>
                </div>
              )}

              {formData.vehicle_category === 'trailer' && (
                <div className="p-3 bg-slate-50 rounded-lg space-y-3" data-testid="specs-trailer">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Trailer Specifications</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Trailer Type</Label>
                      <Select value={formData.category_specs.trailer_type || ''} onValueChange={v => setSpec('trailer_type', v)}>
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-trailer-type"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="box">Box Trailer</SelectItem>
                          <SelectItem value="flatbed">Flatbed</SelectItem>
                          <SelectItem value="car_carrier">Car Carrier</SelectItem>
                          <SelectItem value="enclosed">Enclosed</SelectItem>
                          <SelectItem value="tipper">Tipper</SelectItem>
                          <SelectItem value="boat_trailer">Boat Trailer</SelectItem>
                          <SelectItem value="camper">Camper</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Load Capacity (kg)</Label>
                      <Input type="number" min={0} placeholder="2000" className="mt-1 h-9 text-sm" data-testid="spec-load-capacity"
                        value={formData.category_specs.load_capacity || ''} onChange={e => setSpec('load_capacity', e.target.value ? parseInt(e.target.value) : '')} />
                    </div>
                    <div>
                      <Label className="text-xs">Braking</Label>
                      <Select value={formData.category_specs.braked_or_unbraked || ''} onValueChange={v => setSpec('braked_or_unbraked', v)}>
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-braking"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="braked">Braked</SelectItem>
                          <SelectItem value="unbraked">Unbraked</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {(formData.vehicle_category === 'ebike' || formData.vehicle_category === 'electric_scooter') && (
                <div className="p-3 bg-slate-50 rounded-lg space-y-3" data-testid={`specs-${formData.vehicle_category}`}>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    {formData.vehicle_category === 'ebike' ? 'eBike' : 'Electric Scooter'} Specifications
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Battery Range (km)</Label>
                      <Input type="number" min={0} placeholder="50" className="mt-1 h-9 text-sm" data-testid="spec-battery-range"
                        value={formData.category_specs.battery_range_km || ''} onChange={e => setSpec('battery_range_km', e.target.value ? parseInt(e.target.value) : '')} />
                    </div>
                    <div>
                      <Label className="text-xs">Top Speed (km/h)</Label>
                      <Input type="number" min={0} placeholder="25" className="mt-1 h-9 text-sm" data-testid="spec-top-speed"
                        value={formData.category_specs.top_speed_kmh || ''} onChange={e => setSpec('top_speed_kmh', e.target.value ? parseInt(e.target.value) : '')} />
                    </div>
                    <div>
                      <Label className="text-xs">Rider Capacity</Label>
                      <Input type="number" min={1} max={2} placeholder="1" className="mt-1 h-9 text-sm" data-testid="spec-rider-cap"
                        value={formData.category_specs.rider_capacity || ''} onChange={e => setSpec('rider_capacity', e.target.value ? parseInt(e.target.value) : '')} />
                    </div>
                  </div>
                </div>
              )}

              {formData.vehicle_category === 'mobility_scooter' && (
                <div className="p-3 bg-slate-50 rounded-lg space-y-3" data-testid="specs-mobility_scooter">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Mobility Scooter Specifications</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Battery Range (km)</Label>
                      <Input type="number" min={0} placeholder="30" className="mt-1 h-9 text-sm" data-testid="spec-ms-battery-range"
                        value={formData.category_specs.battery_range_km || ''} onChange={e => setSpec('battery_range_km', e.target.value ? parseInt(e.target.value) : '')} />
                    </div>
                    <div>
                      <Label className="text-xs">Max Speed (km/h)</Label>
                      <Input type="number" min={0} placeholder="12" className="mt-1 h-9 text-sm" data-testid="spec-ms-max-speed"
                        value={formData.category_specs.top_speed_kmh || ''} onChange={e => setSpec('top_speed_kmh', e.target.value ? parseInt(e.target.value) : '')} />
                    </div>
                    <div>
                      <Label className="text-xs">Weight Capacity (kg)</Label>
                      <Input type="number" min={0} placeholder="150" className="mt-1 h-9 text-sm" data-testid="spec-ms-weight-capacity"
                        value={formData.category_specs.weight_capacity_kg || ''} onChange={e => setSpec('weight_capacity_kg', e.target.value ? parseInt(e.target.value) : '')} />
                    </div>
                    <div className="flex items-center gap-2 pt-4">
                      <input type="checkbox" id="ms-foldable" className="rounded border-slate-300" data-testid="spec-ms-foldable"
                        checked={formData.category_specs.foldable || false} onChange={e => setSpec('foldable', e.target.checked)} />
                      <Label htmlFor="ms-foldable" className="text-xs cursor-pointer">Foldable</Label>
                    </div>
                    <div className="flex items-center gap-2 pt-4">
                      <input type="checkbox" id="ms-transport-friendly" className="rounded border-slate-300" data-testid="spec-ms-transport-friendly"
                        checked={formData.category_specs.transport_friendly || false} onChange={e => setSpec('transport_friendly', e.target.checked)} />
                      <Label htmlFor="ms-transport-friendly" className="text-xs cursor-pointer">Transport Friendly</Label>
                    </div>
                  </div>
                </div>
              )}

              {formData.vehicle_category === 'specialty_asset' && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-100 space-y-3" data-testid="specs-specialty_asset">
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Specialty Asset Details</p>
                  <p className="text-xs text-amber-700/80 -mt-1">
                    Best suited for events, private functions, and controlled use.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Asset Type *</Label>
                      <Select
                        value={formData.category_specs.asset_type || 'coffee_trailer'}
                        onValueChange={v => setSpec('asset_type', v)}
                      >
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-asset-type">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="coffee_trailer">Coffee / Drinks Trailer</SelectItem>
                          <SelectItem value="food_trailer_van">Food Trailer / Van</SelectItem>
                          <SelectItem value="dessert_setup">Dessert / Ice Cream Setup</SelectItem>
                          <SelectItem value="bar_trailer">Bar Trailer / Beverage Setup</SelectItem>
                          <SelectItem value="event_trailer">Event Trailer / Activation Trailer</SelectItem>
                          <SelectItem value="market_stall_popup">Market Stall / Pop-Up Setup</SelectItem>
                          <SelectItem value="photo_booth_media">Photo Booth / Media Setup</SelectItem>
                          <SelectItem value="equipment_package">Equipment Hire Package</SelectItem>
                          <SelectItem value="furniture_package">Furniture / Decor Package</SelectItem>
                          <SelectItem value="cool_room_refrigeration">Cool Room / Refrigeration Trailer</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Usage Type *</Label>
                      <Select
                        value={formData.category_specs.usage_type || 'pickup_by_renter'}
                        onValueChange={v => setSpec('usage_type', v)}
                      >
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-usage-type">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pickup_by_renter">Pickup by Renter</SelectItem>
                          <SelectItem value="delivered_by_host">Delivered by Host</SelectItem>
                          <SelectItem value="both">Both Options Available</SelectItem>
                          <SelectItem value="operator_staffed_only">Operator / Staffed Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Service Model</Label>
                      <Select
                        value={formData.category_specs.service_model || 'self_hire'}
                        onValueChange={v => setSpec('service_model', v)}
                      >
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-service-model">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="self_hire">Self hire</SelectItem>
                          <SelectItem value="host_delivery_only">Host delivery only</SelectItem>
                          <SelectItem value="staffed_service">Staffed service</SelectItem>
                          <SelectItem value="flexible">Flexible</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Operating Environment</Label>
                      <Select
                        value={formData.category_specs.operating_environment || 'both'}
                        onValueChange={v => setSpec('operating_environment', v)}
                      >
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-operating-environment">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="indoor_only">Indoor only</SelectItem>
                          <SelectItem value="outdoor_only">Outdoor only</SelectItem>
                          <SelectItem value="both">Both</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="sa-event-only"
                      className="rounded border-slate-300"
                      data-testid="spec-event-only"
                      checked={formData.category_specs.event_only !== false}
                      onChange={e => setSpec('event_only', e.target.checked)}
                    />
                    <Label htmlFor="sa-event-only" className="text-xs cursor-pointer">
                      Event / private function use only
                    </Label>
                  </div>
                  <div>
                    <Label className="text-xs">Equipment Included</Label>
                    <Textarea
                      rows={2}
                      placeholder="e.g., 2-group espresso machine, grinder, milk steamer, milkshake setup, 2x insulated urns"
                      className="mt-1 text-sm"
                      data-testid="spec-includes-equipment"
                      value={formData.category_specs.includes_equipment || ''}
                      onChange={e => setSpec('includes_equipment', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">What the Renter Must Supply</Label>
                    <Textarea
                      rows={2}
                      placeholder="e.g., consumables (coffee beans, milk, syrups, cups), staff, public liability insurance, council/event permits"
                      className="mt-1 text-sm"
                      data-testid="spec-renter-must-supply"
                      value={formData.category_specs.renter_must_supply || ''}
                      onChange={e => setSpec('renter_must_supply', e.target.value)}
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      Anything the renter/operator is responsible for bringing or arranging themselves. Helps avoid surprises on the day.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Setup Notes</Label>
                    <Textarea
                      rows={2}
                      placeholder="e.g., 10A power required, 2.5m x 4m footprint, level ground"
                      className="mt-1 text-sm"
                      data-testid="spec-setup-notes"
                      value={formData.category_specs.setup_notes || ''}
                      onChange={e => setSpec('setup_notes', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Optional Add-ons Available</Label>
                    <Textarea
                      rows={2}
                      placeholder="e.g., generator hire, syrups, ice cream stock, setup & packdown service"
                      className="mt-1 text-sm"
                      data-testid="spec-addons-available"
                      value={formData.category_specs.addons_available || ''}
                      onChange={e => setSpec('addons_available', e.target.value)}
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      Listed as a guide — arrange pricing and availability directly with the renter.
                    </p>
                  </div>

                  {/* Event Timing & Delivery Logistics — specialty-only.
                      Stored inside category_specs (no schema change). All
                      fields are optional/additive; existing listings render
                      unchanged when these aren't set. */}
                  <div className="pt-3 mt-1 border-t border-amber-200 space-y-3" data-testid="specs-event-delivery">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                      Event Timing &amp; Delivery
                    </p>

                    {/* Event Time */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Typical Event Start Time</Label>
                        <Input
                          type="time"
                          className="mt-1 h-9 text-sm"
                          data-testid="spec-event-start-time"
                          value={formData.category_specs.event_start_time || ''}
                          onChange={e => setSpec('event_start_time', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Typical Event End Time</Label>
                        <Input
                          type="time"
                          className="mt-1 h-9 text-sm"
                          data-testid="spec-event-end-time"
                          value={formData.category_specs.event_end_time || ''}
                          onChange={e => setSpec('event_end_time', e.target.value)}
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500 -mt-1">
                      Optional — guides renters on the operating window. Setup &amp; pack-down sit outside this window.
                    </p>

                    {/* Setup Responsibility */}
                    <div>
                      <Label className="text-xs">Who Handles Setup &amp; Pack-down?</Label>
                      <Select
                        value={formData.category_specs.setup_responsibility || ''}
                        onValueChange={v => setSpec('setup_responsibility', v)}
                      >
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-setup-responsibility">
                          <SelectValue placeholder="Select responsibility" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="host">Host handles setup &amp; pack-down</SelectItem>
                          <SelectItem value="renter">Renter handles setup &amp; pack-down</SelectItem>
                          <SelectItem value="shared">Shared — host delivers, renter operates</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Delivery Structure */}
                    <div className="rounded-md border border-amber-200 bg-white p-3 space-y-3" data-testid="spec-delivery-structure">
                      <p className="text-xs font-semibold text-slate-700">Delivery Structure</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-0.5 rounded border-slate-300"
                            data-testid="spec-delivery-available"
                            checked={!!formData.category_specs.delivery_available}
                            onChange={e => setSpec('delivery_available', e.target.checked)}
                          />
                          <span className="text-xs">Delivery available</span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-0.5 rounded border-slate-300"
                            data-testid="spec-pickup-available"
                            checked={!!formData.category_specs.pickup_available}
                            onChange={e => setSpec('pickup_available', e.target.checked)}
                          />
                          <span className="text-xs">Renter pickup available</span>
                        </label>
                      </div>
                      {formData.category_specs.delivery_available && (
                        <div>
                          <Label className="text-xs">Delivery Fee (AUD, optional)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="e.g., 80"
                            className="mt-1 h-9 text-sm"
                            data-testid="spec-delivery-fee"
                            value={formData.category_specs.delivery_fee ?? ''}
                            onChange={e => setSpec('delivery_fee', e.target.value === '' ? '' : parseFloat(e.target.value))}
                          />
                          <p className="text-[10px] text-slate-500 mt-1">
                            Leave blank if delivery cost is quoted on request.
                          </p>
                        </div>
                      )}
                      <div>
                        <Label className="text-xs">Delivery / Pickup Notes</Label>
                        <Textarea
                          rows={2}
                          placeholder="e.g., Delivery within 25 km of Sydney CBD included. Beyond that, $2/km. Pickup available from Marrickville depot."
                          className="mt-1 text-sm"
                          data-testid="spec-delivery-notes"
                          value={formData.category_specs.delivery_notes || ''}
                          onChange={e => setSpec('delivery_notes', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Power & Setup Requirements */}
                  <div className="pt-3 mt-1 border-t border-amber-200 space-y-3" data-testid="specs-power-setup">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                      Power &amp; Setup Requirements
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Power Source</Label>
                        <Select
                          value={formData.category_specs.power_source_type || 'external_power'}
                          onValueChange={v => setSpec('power_source_type', v)}
                        >
                          <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-power-source-type">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="external_power">External power (mains)</SelectItem>
                            <SelectItem value="generator">Generator</SelectItem>
                            <SelectItem value="both_supported">Both supported</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Power Inlet Type</Label>
                        <Select
                          value={formData.category_specs.power_inlet_type || '10A'}
                          onValueChange={v => setSpec('power_inlet_type', v)}
                        >
                          <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-power-inlet-type">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10A">10A (standard)</SelectItem>
                            <SelectItem value="15A">15A</SelectItem>
                            <SelectItem value="dual_15A">Dual 15A</SelectItem>
                            <SelectItem value="32A">32A</SelectItem>
                            <SelectItem value="custom">Custom (see notes)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Number of Power Inlets</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          placeholder="e.g., 2"
                          className="mt-1 h-9 text-sm"
                          data-testid="spec-number-of-power-inlets"
                          value={formData.category_specs.number_of_power_inlets ?? ''}
                          onChange={e => setSpec('number_of_power_inlets', e.target.value === '' ? '' : Number(e.target.value))}
                        />
                      </div>
                      <div className="flex items-end">
                        <div className="flex items-center gap-2 h-9">
                          <input
                            type="checkbox"
                            id="sa-separate-circuits"
                            className="rounded border-slate-300"
                            data-testid="spec-requires-separate-circuits"
                            checked={!!formData.category_specs.requires_separate_circuits}
                            onChange={e => setSpec('requires_separate_circuits', e.target.checked)}
                          />
                          <Label htmlFor="sa-separate-circuits" className="text-xs cursor-pointer">
                            Requires separate circuits
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">Power Requirement Summary</Label>
                      <Textarea
                        rows={2}
                        placeholder="e.g., 2 × 15A dedicated circuits, total ~20A load at peak"
                        className="mt-1 text-sm"
                        data-testid="spec-power-requirement-summary"
                        value={formData.category_specs.power_requirement_summary || ''}
                        onChange={e => setSpec('power_requirement_summary', e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 h-9">
                        <input
                          type="checkbox"
                          id="sa-gen-supported"
                          className="rounded border-slate-300"
                          data-testid="spec-generator-supported"
                          checked={!!formData.category_specs.generator_supported}
                          onChange={e => setSpec('generator_supported', e.target.checked)}
                        />
                        <Label htmlFor="sa-gen-supported" className="text-xs cursor-pointer">
                          Generator use supported
                        </Label>
                      </div>
                      <div className="flex items-center gap-2 h-9">
                        <input
                          type="checkbox"
                          id="sa-gen-included"
                          className="rounded border-slate-300"
                          data-testid="spec-generator-included"
                          checked={!!formData.category_specs.generator_included}
                          onChange={e => setSpec('generator_included', e.target.checked)}
                        />
                        <Label htmlFor="sa-gen-included" className="text-xs cursor-pointer">
                          Generator included in hire
                        </Label>
                      </div>
                    </div>

                    {(formData.category_specs.generator_supported || formData.category_specs.generator_included) && (
                      <div>
                        <Label className="text-xs">Generator Notes</Label>
                        <Textarea
                          rows={2}
                          placeholder="e.g., min 3.5kVA inverter generator required, Honda EU30i or equivalent"
                          className="mt-1 text-sm"
                          data-testid="spec-generator-spec-notes"
                          value={formData.category_specs.generator_spec_notes || ''}
                          onChange={e => setSpec('generator_spec_notes', e.target.value)}
                        />
                      </div>
                    )}

                    {/* P0: Host-editable Power Requirements text — shown on listing under Power section */}
                    <div>
                      <Label className="text-xs flex items-center gap-1">
                        <Zap className="w-3 h-3 text-yellow-600" /> Power Requirements (renter-visible)
                      </Label>
                      <Textarea
                        rows={2}
                        placeholder="e.g., Requires 2 × 15A dedicated circuits. Not suitable for standard household outlets. Quiet inverter generator recommended."
                        className="mt-1 text-sm"
                        data-testid="spec-power-requirements-text"
                        value={formData.category_specs.power_requirements_text || ''}
                        onChange={e => setSpec('power_requirements_text', e.target.value)}
                      />
                      <p className="text-[11px] text-slate-500 mt-1">Renters see this inside the listing's Power &amp; Setup section.</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                      <div>
                        <Label className="text-xs">Setup Time (minutes)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="5"
                          placeholder="e.g., 30"
                          className="mt-1 h-9 text-sm"
                          data-testid="spec-setup-time-minutes"
                          value={formData.category_specs.setup_time_minutes ?? ''}
                          onChange={e => setSpec('setup_time_minutes', e.target.value === '' ? '' : Number(e.target.value))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Packdown Time (minutes)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="5"
                          placeholder="e.g., 20"
                          className="mt-1 h-9 text-sm"
                          data-testid="spec-packdown-time-minutes"
                          value={formData.category_specs.packdown_time_minutes ?? ''}
                          onChange={e => setSpec('packdown_time_minutes', e.target.value === '' ? '' : Number(e.target.value))}
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">Access Requirements</Label>
                      <Textarea
                        rows={2}
                        placeholder="e.g., vehicle access within 10m of setup area, flat gradient, turning circle 5m"
                        className="mt-1 text-sm"
                        data-testid="spec-access-requirements"
                        value={formData.category_specs.access_requirements || ''}
                        onChange={e => setSpec('access_requirements', e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                      <div className="flex items-center gap-2 h-9">
                        <input
                          type="checkbox"
                          id="sa-food-service"
                          className="rounded border-slate-300"
                          data-testid="spec-food-service-ready"
                          checked={!!formData.category_specs.food_service_ready}
                          onChange={e => setSpec('food_service_ready', e.target.checked)}
                        />
                        <Label htmlFor="sa-food-service" className="text-xs cursor-pointer">
                          Food-service ready
                        </Label>
                      </div>
                      <div className="flex items-center gap-2 h-9">
                        <input
                          type="checkbox"
                          id="sa-requires-permits"
                          className="rounded border-slate-300"
                          data-testid="spec-requires-permits"
                          checked={!!formData.category_specs.requires_permits}
                          onChange={e => setSpec('requires_permits', e.target.checked)}
                        />
                        <Label htmlFor="sa-requires-permits" className="text-xs cursor-pointer">
                          Event permits may be required
                        </Label>
                      </div>
                    </div>

                    {/* Trailer-specific technical fields — only for trailer-based asset types */}
                    {['coffee_trailer','food_trailer_van','dessert_setup','bar_trailer','event_trailer','cool_room_refrigeration'].includes(formData.category_specs.asset_type) && (
                      <div className="pt-3 border-t border-amber-200 space-y-3" data-testid="specs-trailer-technical">
                        <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                          Trailer Specifications
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Length (m)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.1"
                              placeholder="e.g., 4.0"
                              className="mt-1 h-9 text-sm"
                              data-testid="spec-overall-length-m"
                              value={formData.category_specs.overall_length_m ?? ''}
                              onChange={e => setSpec('overall_length_m', e.target.value === '' ? '' : Number(e.target.value))}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Width (m)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.1"
                              placeholder="e.g., 2.5"
                              className="mt-1 h-9 text-sm"
                              data-testid="spec-overall-width-m"
                              value={formData.category_specs.overall_width_m ?? ''}
                              onChange={e => setSpec('overall_width_m', e.target.value === '' ? '' : Number(e.target.value))}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Height (m)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.1"
                              placeholder="e.g., 2.8"
                              className="mt-1 h-9 text-sm"
                              data-testid="spec-overall-height-m"
                              value={formData.category_specs.overall_height_m ?? ''}
                              onChange={e => setSpec('overall_height_m', e.target.value === '' ? '' : Number(e.target.value))}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Tare (kg)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="10"
                              placeholder="e.g., 750"
                              className="mt-1 h-9 text-sm"
                              data-testid="spec-tare-kg"
                              value={formData.category_specs.tare_kg ?? ''}
                              onChange={e => setSpec('tare_kg', e.target.value === '' ? '' : Number(e.target.value))}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Axle Type</Label>
                            <Select
                              value={formData.category_specs.axle_type || 'single'}
                              onValueChange={v => setSpec('axle_type', v)}
                            >
                              <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-axle-type">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="single">Single</SelectItem>
                                <SelectItem value="dual">Dual</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Brake Type</Label>
                            <Select
                              value={formData.category_specs.brake_type || 'none'}
                              onValueChange={v => setSpec('brake_type', v)}
                            >
                              <SelectTrigger className="mt-1 h-9 text-sm" data-testid="spec-brake-type">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="mechanical">Mechanical (override)</SelectItem>
                                <SelectItem value="electric">Electric</SelectItem>
                                <SelectItem value="hydraulic">Hydraulic</SelectItem>
                                <SelectItem value="none">None</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* P0: Specialty Rules — host-controllable per-listing toggles.
                  Stored at `vehicle.specialty_rules` (top level, not in
                  category_specs). Read by SpecialtyRulesCard for rendering. */}
              {formData.vehicle_category === 'specialty_asset' && (
                <div className="p-3 bg-white border border-amber-200 rounded-lg space-y-3" data-testid="specialty-rules-section">
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Specialty Rules</p>
                    <p className="text-xs text-slate-500">Tick only the rules that apply to this listing. Renters will see the enabled items.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { key: 'event_only', label: 'Event / private function use only', defaultOn: true },
                      { key: 'no_public_trading', label: 'No unauthorised public trading', defaultOn: true },
                      { key: 'no_modifications', label: 'No modification of equipment', defaultOn: true },
                      { key: 'must_return_clean', label: 'Must be returned cleaned & repacked', defaultOn: true },
                      { key: 'damage_reporting_required', label: 'Damage must be reported immediately', defaultOn: true },
                      { key: 'follow_power_requirements', label: 'Renter must follow power requirements', defaultOn: true },
                      { key: 'follow_setup_packdown', label: 'Renter must follow setup & packdown instructions', defaultOn: true },
                      { key: 'renter_responsible_permits', label: 'Renter is responsible for permits / approvals', defaultOn: false },
                      { key: 'renter_requires_insurance', label: 'Renter must hold public liability insurance', defaultOn: false },
                    ].map(rule => {
                      const raw = formData.specialty_rules?.[rule.key];
                      const checked = (raw === undefined) ? rule.defaultOn : !!raw;
                      return (
                        <label key={rule.key} className="flex items-start gap-2 text-sm cursor-pointer p-2 hover:bg-slate-50 rounded">
                          <input
                            type="checkbox"
                            className="mt-0.5 rounded border-slate-300"
                            checked={checked}
                            onChange={e => setSpecialtyRule(rule.key, e.target.checked)}
                            data-testid={`specialty-rule-${rule.key.replace(/_/g,'-')}-toggle`}
                          />
                          <span className="text-slate-700">{rule.label}</span>
                        </label>
                      );
                    })}
                  </div>

                  <div>
                    <Label className="text-xs">Custom specialty rules (renter-visible)</Label>
                    <Textarea
                      rows={3}
                      placeholder="Additional rules specific to this asset — e.g., quiet hours, food safety, venue restrictions."
                      className="mt-1 text-sm"
                      data-testid="specialty-custom-rules-text"
                      value={formData.specialty_rules?.custom_rules_text || ''}
                      onChange={e => setSpecialtyRule('custom_rules_text', e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="location" className="text-sm">Location *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Sydney CBD, NSW"
                  required
                  className="mt-1.5 h-10 sm:h-11"
                  data-testid="location-input"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-sm">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your vehicle, features, and any special notes..."
                  rows={3}
                  className="mt-1.5"
                  data-testid="description-input"
                />
                {formData.vehicle_category === 'specialty_asset' && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-800" data-testid="specialty-description-tip">
                    <p className="font-semibold mb-1">Tip — include:</p>
                    <ul className="list-disc pl-4 space-y-0.5 text-amber-700">
                      <li>Type of events this suits (weddings, parties, corporate)</li>
                      <li>What's included (equipment, setup, service items)</li>
                      <li>What the renter must provide (power, staff, permits)</li>
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Registration & Safety Controls - P0 Required */}
          <Card className="border-slate-100 border-l-4 border-l-amber-500">
            <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
              <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-600" />
                Registration & Safety Controls
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm text-slate-500 mt-1">
                Required for legal compliance. Vehicles with expired registration cannot be listed publicly.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-4">
              {/* Rego Expiry Warning Banner */}
              {formData.rego_expiry_date && (() => {
                const today = new Date();
                const expiryDate = parseISO(formData.rego_expiry_date);
                const daysUntil = differenceInDays(expiryDate, today);
                
                if (daysUntil < 0) {
                  return (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Registration Expired</p>
                        <p className="text-xs text-red-600 mt-0.5">
                          This vehicle is hidden from public listings. Update the registration to make it visible again.
                        </p>
                      </div>
                    </div>
                  );
                } else if (daysUntil <= 7) {
                  return (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Registration Expires Soon!</p>
                        <p className="text-xs text-red-600 mt-0.5">
                          Expires in {daysUntil} day{daysUntil !== 1 ? 's' : ''}. Update immediately to avoid being hidden from listings.
                        </p>
                      </div>
                    </div>
                  );
                } else if (daysUntil <= 30) {
                  return (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800">Registration Expiring</p>
                        <p className="text-xs text-yellow-600 mt-0.5">
                          Expires in {daysUntil} days. Consider renewing soon to avoid disruption.
                        </p>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Registration State */}
                <div>
                  <Label className="text-sm">Registration State *</Label>
                  <Select 
                    value={formData.rego_state} 
                    onValueChange={(value) => setFormData({ ...formData, rego_state: value })}
                  >
                    <SelectTrigger className="mt-1.5 h-10 sm:h-11" data-testid="rego-state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {AUSTRALIAN_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>{state.value} - {state.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Registration Expiry Date */}
                <div className="sm:col-span-2">
                  <Label className="text-sm">Registration Expiry Date *</Label>
                  <Popover open={regoDatePickerOpen} onOpenChange={setRegoDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full mt-1.5 h-10 sm:h-11 justify-start text-left font-normal ${!formData.rego_expiry_date ? 'text-slate-500' : ''}`}
                        data-testid="rego-expiry-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.rego_expiry_date 
                          ? format(parseISO(formData.rego_expiry_date), 'dd MMM yyyy')
                          : 'Select expiry date'
                        }
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.rego_expiry_date ? parseISO(formData.rego_expiry_date) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            setFormData({ ...formData, rego_expiry_date: format(date, 'yyyy-MM-dd') });
                          }
                          setRegoDatePickerOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Cancellation Policy */}
              <div>
                <Label className="text-sm">Cancellation Policy *</Label>
                <div className="mt-2 space-y-2">
                  {CANCELLATION_POLICIES.map((policy) => (
                    <div 
                      key={policy.value}
                      onClick={() => setFormData({ ...formData, cancellation_policy: policy.value })}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.cancellation_policy === policy.value 
                          ? 'border-[#004B8D] bg-[#004B8D]/5' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`policy-${policy.value}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            formData.cancellation_policy === policy.value 
                              ? 'border-[#004B8D]' 
                              : 'border-slate-300'
                          }`}>
                            {formData.cancellation_policy === policy.value && (
                              <div className="w-2 h-2 rounded-full bg-[#004B8D]" />
                            )}
                          </div>
                          <span className="font-medium text-sm">{policy.label}</span>
                        </div>
                        <Badge variant="outline" className={`text-xs ${
                          policy.value === 'flexible' ? 'border-green-300 text-green-700' :
                          policy.value === 'moderate' ? 'border-yellow-300 text-yellow-700' :
                          'border-red-300 text-red-700'
                        }`}>
                          {policy.value === 'flexible' ? 'Renter Friendly' :
                           policy.value === 'moderate' ? 'Balanced' :
                           'Host Friendly'}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1.5 ml-6">{policy.description}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-3 bg-slate-50 rounded-lg flex items-start gap-2">
                  <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-600">
                    The cancellation policy determines how much renters are refunded when they cancel. 
                    Bond is always refunded regardless of policy. Platform fees are non-refundable.
                  </p>
                </div>
              </div>

              {/* Insurance Compliance Section - P1 */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h4 className="font-medium text-slate-800">Vehicle Insurance</h4>
                  {formData.insurance_expiry_date && (() => {
                    const today = new Date();
                    const expiryDate = parseISO(formData.insurance_expiry_date);
                    const daysUntil = differenceInDays(expiryDate, today);
                    
                    if (daysUntil < 0) {
                      return <Badge className="bg-red-100 text-red-800">Expired</Badge>;
                    } else if (daysUntil <= 7) {
                      return <Badge className="bg-red-100 text-red-800">Expires in {daysUntil}d</Badge>;
                    } else if (daysUntil <= 30) {
                      return <Badge className="bg-amber-100 text-amber-800">Expires in {daysUntil}d</Badge>;
                    } else {
                      return <Badge className="bg-green-100 text-green-800">Valid</Badge>;
                    }
                  })()}
                  {!formData.insurance_expiry_date && (
                    <Badge className="bg-amber-100 text-amber-800">Missing</Badge>
                  )}
                </div>

                {/* Insurance Warning Banner */}
                {formData.insurance_expiry_date && (() => {
                  const today = new Date();
                  const expiryDate = parseISO(formData.insurance_expiry_date);
                  const daysUntil = differenceInDays(expiryDate, today);
                  
                  if (daysUntil < 0) {
                    return (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-800">Insurance Expired</p>
                          <p className="text-xs text-red-600 mt-0.5">
                            Please update your insurance details to ensure compliance.
                          </p>
                        </div>
                      </div>
                    );
                  } else if (daysUntil <= 7) {
                    return (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-800">Insurance Expires Soon!</p>
                          <p className="text-xs text-red-600 mt-0.5">
                            Expires in {daysUntil} day{daysUntil !== 1 ? 's' : ''}. Update immediately.
                          </p>
                        </div>
                      </div>
                    );
                  } else if (daysUntil <= 30) {
                    return (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-yellow-800">Insurance Expiring</p>
                          <p className="text-xs text-yellow-600 mt-0.5">
                            Expires in {daysUntil} days. Consider renewing soon.
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {!formData.insurance_expiry_date && !formData.insurance_provider && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                    <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Insurance Details Missing</p>
                      <p className="text-xs text-amber-600 mt-0.5">
                        Add insurance details to ensure your vehicle is fully compliant.
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Insurance Provider */}
                  <div>
                    <Label className="text-sm">Insurance Provider</Label>
                    <Input
                      value={formData.insurance_provider}
                      onChange={(e) => setFormData({ ...formData, insurance_provider: e.target.value })}
                      placeholder="e.g., NRMA, AAMI, Allianz"
                      className="mt-1.5 h-10 sm:h-11"
                      data-testid="insurance-provider-input"
                    />
                  </div>

                  {/* Policy Number */}
                  <div>
                    <Label className="text-sm">Policy / Reference Number</Label>
                    <Input
                      value={formData.insurance_policy_number}
                      onChange={(e) => setFormData({ ...formData, insurance_policy_number: e.target.value })}
                      placeholder="e.g., POL-123456789"
                      className="mt-1.5 h-10 sm:h-11"
                      data-testid="insurance-policy-input"
                    />
                  </div>
                </div>

                {/* Insurance Expiry Date */}
                <div className="mt-4">
                  <Label className="text-sm">Insurance Expiry Date</Label>
                  <Popover open={insuranceDatePickerOpen} onOpenChange={setInsuranceDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full mt-1.5 h-10 sm:h-11 justify-start text-left font-normal ${!formData.insurance_expiry_date ? 'text-slate-500' : ''}`}
                        data-testid="insurance-expiry-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.insurance_expiry_date 
                          ? format(parseISO(formData.insurance_expiry_date), 'dd MMM yyyy')
                          : 'Select insurance expiry date'
                        }
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.insurance_expiry_date ? parseISO(formData.insurance_expiry_date) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            setFormData({ ...formData, insurance_expiry_date: format(date, 'yyyy-MM-dd') });
                          }
                          setInsuranceDatePickerOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Insurance Certificate Upload */}
                {isEditing && (
                  <div className="mt-4">
                    <Label className="text-sm">Insurance Certificate</Label>
                    <div className="mt-2">
                      {formData.insurance_document_s3_key ? (
                        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-green-800">Certificate Uploaded</p>
                            <p className="text-xs text-green-600">Document is on file</p>
                          </div>
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                setInsuranceUploading(true);
                                try {
                                  const formDataUpload = new FormData();
                                  formDataUpload.append('file', file);
                                  
                                  const res = await api.post(`/vehicles/${vehicleId}/insurance/upload`, formDataUpload, {
                                    headers: { 'Content-Type': undefined }
                                  });
                                  
                                  setFormData(prev => ({ ...prev, insurance_document_s3_key: res.data.s3_key }));
                                  toast.success('Insurance certificate updated');
                                } catch (err) {
                                  toast.error('Failed to upload certificate');
                                } finally {
                                  setInsuranceUploading(false);
                                }
                              }}
                            />
                            <Button type="button" variant="outline" size="sm" disabled={insuranceUploading}>
                              {insuranceUploading ? 'Uploading...' : 'Replace'}
                            </Button>
                          </label>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              setInsuranceUploading(true);
                              try {
                                const formDataUpload = new FormData();
                                formDataUpload.append('file', file);
                                
                                const res = await api.post(`/vehicles/${vehicleId}/insurance/upload`, formDataUpload, {
                                  headers: { 'Content-Type': undefined }
                                });
                                
                                setFormData(prev => ({ ...prev, insurance_document_s3_key: res.data.s3_key }));
                                toast.success('Insurance certificate uploaded');
                              } catch (err) {
                                toast.error('Failed to upload certificate');
                              } finally {
                                setInsuranceUploading(false);
                              }
                            }}
                          />
                          <div className="flex items-center gap-3 p-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 transition-colors">
                            <Upload className="w-6 h-6 text-slate-400" />
                            <div>
                              <p className="text-sm font-medium text-slate-700">
                                {insuranceUploading ? 'Uploading...' : 'Upload Insurance Certificate'}
                              </p>
                              <p className="text-xs text-slate-500">PDF, JPG, PNG (max 10MB)</p>
                            </div>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* GPS Tracker Section - P2 Security & Recovery */}
          {isEditing && vehicleId && (
            <TrackerSection 
              vehicle={formData}
              onUpdate={fetchVehicle}
              isPlatformOwner={user?.is_platform_owner}
            />
          )}

          {/* Pricing */}
          <Card className="border-slate-100">
            <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
              <CardTitle className="font-heading text-base sm:text-lg">Pricing & Terms</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-6">
              {/* Daily Rate - Uses NumericInput for decimal */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="daily_rate" className="text-sm font-medium">Daily Rate ($) *</Label>
                  <NumericInput
                    id="daily_rate"
                    allowDecimal={true}
                    value={formData.daily_rate}
                    onChange={(val) => setFormData({ ...formData, daily_rate: val })}
                    placeholder="85.00"
                    required
                    className="mt-1.5 h-11"
                    data-testid="daily-rate-input"
                  />
                </div>
                <div>
                  <Label htmlFor="bond" className="text-sm font-medium">Security Bond ($)</Label>
                  <div className="mt-1.5 space-y-2">
                    <div className="flex gap-2 flex-wrap">
                      {[0, 300, 500, 1000].map((preset) => (
                        <Button
                          key={preset}
                          type="button"
                          variant={parseFloat(formData.bond_amount) === preset ? "default" : "outline"}
                          size="sm"
                          onClick={() => setFormData({ ...formData, bond_amount: preset.toString() })}
                          className="text-xs"
                        >
                          {preset === 0 ? 'No Bond' : `$${preset}`}
                        </Button>
                      ))}
                    </div>
                    <NumericInput
                      id="bond"
                      allowDecimal={true}
                      max={5000}
                      value={formData.bond_amount}
                      onChange={(val) => setFormData({ ...formData, bond_amount: val })}
                      placeholder="Custom amount..."
                      className="h-11"
                    />
                  </div>
                </div>
              </div>

              {/* Stepper Controls for Integer Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Min Days</Label>
                  <StepperInput
                    value={formData.min_booking_days}
                    onChange={(val) => setFormData({ ...formData, min_booking_days: val })}
                    min={1}
                    max={30}
                    data-testid="min-days-stepper"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Max Days</Label>
                  <StepperInput
                    value={formData.max_booking_days}
                    onChange={(val) => setFormData({ ...formData, max_booking_days: val })}
                    min={1}
                    max={365}
                    data-testid="max-days-stepper"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Minimum Driver Age</Label>
                  <StepperInput
                    value={formData.minimum_driver_age}
                    onChange={(val) => setFormData({ ...formData, minimum_driver_age: val })}
                    min={18}
                    max={75}
                    disabled={!ruleOverrides.minimum_driver_age && hostDefaults}
                    data-testid="min-age-stepper"
                  />
                  {hostDefaults && (
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs text-slate-500">
                        Host Default: {hostDefaults.minimum_driver_age}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const newOverrides = { ...ruleOverrides, minimum_driver_age: !ruleOverrides.minimum_driver_age };
                          setRuleOverrides(newOverrides);
                          if (!newOverrides.minimum_driver_age) {
                            setFormData({ ...formData, minimum_driver_age: hostDefaults.minimum_driver_age });
                          }
                        }}
                        className={`text-xs px-2 py-1 rounded ${ruleOverrides.minimum_driver_age ? 'bg-[#004B8D] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                        data-testid="override-min-age"
                      >
                        {ruleOverrides.minimum_driver_age ? 'Custom' : 'Override'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">KMs Included per Day</Label>
                  <StepperInput
                    value={formData.kms_included || 0}
                    onChange={(val) => setFormData({ ...formData, kms_included: val })}
                    min={0}
                    max={1000}
                    step={50}
                    disabled={!ruleOverrides.kms_included && hostDefaults}
                    data-testid="kms-included-stepper"
                  />
                  {hostDefaults && (
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs text-slate-500">
                        Host Default: {hostDefaults.kms_included}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const newOverrides = { ...ruleOverrides, kms_included: !ruleOverrides.kms_included };
                          setRuleOverrides(newOverrides);
                          if (!newOverrides.kms_included) {
                            setFormData({ ...formData, kms_included: hostDefaults.kms_included });
                          }
                        }}
                        className={`text-xs px-2 py-1 rounded ${ruleOverrides.kms_included ? 'bg-[#004B8D] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                        data-testid="override-kms"
                      >
                        {ruleOverrides.kms_included ? 'Custom' : 'Override'}
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-1">Set to 0 for unlimited</p>
                </div>
                <div>
                  <Label htmlFor="excess_km" className="text-sm font-medium">Excess KM Fee ($)</Label>
                  <NumericInput
                    id="excess_km"
                    allowDecimal={true}
                    value={formData.excess_km_fee}
                    onChange={(val) => setFormData({ ...formData, excess_km_fee: val })}
                    placeholder="0.35"
                    className="mt-1.5 h-11"
                    disabled={!ruleOverrides.excess_km_fee && hostDefaults}
                    data-testid="excess-km-input"
                  />
                  {hostDefaults && (
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs text-slate-500">
                        Host Default: ${hostDefaults.excess_km_fee}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const newOverrides = { ...ruleOverrides, excess_km_fee: !ruleOverrides.excess_km_fee };
                          setRuleOverrides(newOverrides);
                          if (!newOverrides.excess_km_fee) {
                            setFormData({ ...formData, excess_km_fee: hostDefaults.excess_km_fee });
                          }
                        }}
                        className={`text-xs px-2 py-1 rounded ${ruleOverrides.excess_km_fee ? 'bg-[#004B8D] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                        data-testid="override-excess-km"
                      >
                        {ruleOverrides.excess_km_fee ? 'Custom' : 'Override'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Fees */}
              <div className="border-t border-slate-100 pt-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Additional Fees (Optional)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="detailing_fee" className="text-sm">Detailing Fee ($)</Label>
                    <NumericInput
                      id="detailing_fee"
                      allowDecimal={true}
                      value={formData.detailing_fee}
                      onChange={(val) => setFormData({ ...formData, detailing_fee: val })}
                      placeholder="50"
                      className="mt-1.5 h-11"
                      disabled={!ruleOverrides.detailing_fee && hostDefaults}
                    />
                    {hostDefaults && (
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-xs text-slate-500">
                          Host Default: ${hostDefaults.detailing_fee || 0}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const newOverrides = { ...ruleOverrides, detailing_fee: !ruleOverrides.detailing_fee };
                            setRuleOverrides(newOverrides);
                            if (!newOverrides.detailing_fee) {
                              setFormData({ ...formData, detailing_fee: hostDefaults.detailing_fee || 0 });
                            }
                          }}
                          className={`text-xs px-2 py-1 rounded ${ruleOverrides.detailing_fee ? 'bg-[#004B8D] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                          data-testid="override-detailing"
                        >
                          {ruleOverrides.detailing_fee ? 'Custom' : 'Override'}
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-1">Charged if vehicle returned dirty</p>
                  </div>
                  <div>
                    <Label htmlFor="late_return_fee" className="text-sm">Late Return Fee ($/hr)</Label>
                    <NumericInput
                      id="late_return_fee"
                      allowDecimal={true}
                      value={formData.late_return_fee}
                      onChange={(val) => setFormData({ ...formData, late_return_fee: val })}
                      placeholder="25"
                      className="mt-1.5 h-11"
                      disabled={!ruleOverrides.late_return_fee && hostDefaults}
                    />
                    {hostDefaults && (
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-xs text-slate-500">
                          Host Default: ${hostDefaults.late_return_fee || 0}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const newOverrides = { ...ruleOverrides, late_return_fee: !ruleOverrides.late_return_fee };
                            setRuleOverrides(newOverrides);
                            if (!newOverrides.late_return_fee) {
                              setFormData({ ...formData, late_return_fee: hostDefaults.late_return_fee || 0 });
                            }
                          }}
                          className={`text-xs px-2 py-1 rounded ${ruleOverrides.late_return_fee ? 'bg-[#004B8D] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                          data-testid="override-late-fee"
                        >
                          {ruleOverrides.late_return_fee ? 'Custom' : 'Override'}
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-1">Per hour late return charge</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Instant Book Toggle */}
          <Card className="border-slate-100">
            <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
              <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Booking Control
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex-1">
                  <p className="font-medium text-sm">Instant Book</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.instant_book 
                      ? 'Bookings are automatically confirmed. Guests can book instantly.'
                      : 'You review and approve each booking request before confirmation.'
                    }
                  </p>
                </div>
                <Switch
                  checked={formData.instant_book}
                  onCheckedChange={handleInstantBookToggle}
                  data-testid="instant-book-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Photos */}
          {isEditing && (
            <Card className="border-slate-100">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
                <CardTitle className="font-heading text-base sm:text-lg">Photos</CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  Upload up to 10 photos (select multiple at once). Drag to reorder. First photo becomes cover image.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {filterValidPhotos(formData.photos).map((photo, index) => (
                    <div 
                      key={index} 
                      className="relative aspect-video bg-slate-100 rounded-lg overflow-hidden group cursor-move"
                      draggable
                      onDragStart={(e) => handlePhotoDragStart(e, index)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handlePhotoDrop(e, index)}
                      data-testid={`photo-preview-${index}`}
                    >
                      <img 
                        src={getVehiclePhotoUrl(photo)}
                        alt={`Vehicle photo ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => handleVehicleImageError(e)}
                      />
                      {/* Cover badge */}
                      {index === 0 && (
                        <Badge className="absolute top-1 left-1 text-[10px] bg-[#004B8D] text-white">Cover</Badge>
                      )}
                      {/* Drag handle */}
                      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <GripVertical className="w-4 h-4 text-white drop-shadow-lg" />
                      </div>
                      {/* Delete button */}
                      <button
                        type="button"
                        onClick={() => handlePhotoDelete(index)}
                        className="absolute bottom-1 right-1 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-photo-${index}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  
                  {/* Upload button - show if less than 10 photos */}
                  {filterValidPhotos(formData.photos).length < 10 && (
                    <label className="aspect-video bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#004B8D] transition-colors">
                      <input 
                        type="file" 
                        accept="image/*" 
                        multiple
                        className="hidden" 
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhoto}
                        data-testid="photo-upload-input"
                      />
                      {uploadingPhoto ? (
                        <div className="flex flex-col items-center">
                          <RefreshCw className="w-6 h-6 text-[#004B8D] animate-spin mb-1" />
                          <span className="text-xs text-slate-500">Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-slate-400 mb-1" />
                          <span className="text-xs text-slate-500">Add Photos</span>
                          <span className="text-[10px] text-slate-400">{filterValidPhotos(formData.photos).length}/10</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Tip: Drag photos to reorder. The first photo will be your listing's cover image.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Listing Video (optional) */}
          {isEditing && (
            <Card className="border-slate-100" data-testid="listing-video-card">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
                <CardTitle className="font-heading text-base sm:text-lg">Listing Video (optional)</CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  Add one short video to show your {formData.vehicle_category === 'specialty_asset' ? 'asset' : 'vehicle'} in motion. Max 50MB. Formats: MP4, MOV, WebM.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                {formData.listing_video_s3_key ? (
                  <div className="space-y-3">
                    <div className="relative aspect-video bg-black rounded-lg overflow-hidden" data-testid="listing-video-preview">
                      <video
                        src={getVehiclePhotoUrl(formData.listing_video_s3_key)}
                        controls
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="video/mp4,video/quicktime,video/webm"
                          className="hidden"
                          onChange={handleVideoUpload}
                          disabled={uploadingVideo}
                          data-testid="listing-video-replace-input"
                        />
                        <Button type="button" variant="outline" size="sm" disabled={uploadingVideo} asChild>
                          <span>{uploadingVideo ? 'Uploading…' : 'Replace video'}</span>
                        </Button>
                      </label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleVideoDelete}
                        disabled={uploadingVideo}
                        data-testid="listing-video-delete-btn"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete video
                      </Button>
                    </div>
                  </div>
                ) : (
                  <label className="block aspect-video bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#004B8D] transition-colors" data-testid="listing-video-upload-label">
                    <input
                      type="file"
                      accept="video/mp4,video/quicktime,video/webm"
                      className="hidden"
                      onChange={handleVideoUpload}
                      disabled={uploadingVideo}
                      data-testid="listing-video-upload-input"
                    />
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      {uploadingVideo ? (
                        <>
                          <RefreshCw className="w-6 h-6 text-[#004B8D] animate-spin mb-1" />
                          <span className="text-xs text-slate-500">Uploading video…</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-slate-400 mb-1" />
                          <span className="text-xs text-slate-500">Add video</span>
                          <span className="text-[10px] text-slate-400 mt-0.5">MP4 · MOV · WebM · ≤50MB</span>
                        </>
                      )}
                    </div>
                  </label>
                )}
              </CardContent>
            </Card>
          )}

          {/* Availability Calendar */}
          {isEditing && (
            <Card className="border-slate-100">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
                <CardTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
                  <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#004B8D]" />
                  Availability Calendar
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-600 mb-3 sm:mb-4">
                      Select dates to block when your vehicle is unavailable:
                    </p>
                    <div className="flex justify-center lg:justify-start">
                      <Calendar
                        mode="multiple"
                        selected={selectedDates}
                        onSelect={setSelectedDates}
                        disabled={(date) => {
                          // P0 FIX: Allow today to be selectable for blocking
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const compareDate = new Date(date);
                          compareDate.setHours(0, 0, 0, 0);
                          return compareDate < today;
                        }}
                        className="rounded-xl border border-slate-200"
                      />
                    </div>
                    
                    {selectedDates.length > 0 && (
                      <div className="mt-4 space-y-3">
                        <Select value={blockReason} onValueChange={setBlockReason}>
                          <SelectTrigger className="h-10 sm:h-11">
                            <SelectValue placeholder="Reason for blocking" />
                          </SelectTrigger>
                          <SelectContent>
                            {blockReasons.map((reason) => (
                              <SelectItem key={reason.value} value={reason.value}>{reason.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button 
                          type="button"
                          onClick={handleBlockDates}
                          className="w-full bg-red-500 hover:bg-red-600 h-10 sm:h-11"
                        >
                          Block {selectedDates.length} Date{selectedDates.length > 1 ? 's' : ''}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-xs sm:text-sm font-medium text-slate-700 mb-3 sm:mb-4">Blocked Dates:</p>
                    {blockedDates.length === 0 ? (
                      <p className="text-xs sm:text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-lg">No dates blocked</p>
                    ) : (
                      <div className="space-y-2 max-h-72 sm:max-h-96 overflow-y-auto">
                        {blockedDates.map((item) => (
                          <div key={item.date} className="flex items-center justify-between p-2.5 sm:p-3 bg-red-50 rounded-lg">
                            <div className="min-w-0">
                              <p className="font-medium text-xs sm:text-sm">{item.date}</p>
                              <p className="text-[10px] sm:text-xs text-slate-500 capitalize">{item.reason || 'Blocked'}</p>
                            </div>
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleUnblockDate(item.date)}
                              className="h-8 w-8 p-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/host/vehicles')}
              className="w-full sm:flex-1 h-11 sm:h-12"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="w-full sm:flex-1 bg-[#004B8D] hover:bg-[#003865] h-11 sm:h-12"
              disabled={loading}
              data-testid="save-vehicle-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Vehicle')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleForm;
